import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { Card, Flex, Spin, Tabs } from 'antd';
import { ErrorDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { useAuth } from 'features/auth/auth-action-sign-in-out';
import { QAPanelTemplate } from 'features/patient/assessment/assessment-controller/_components/questionnaire-qa-panel';
import {
  CoreSignupForm,
  SignupFormProps
} from 'features/sign-in/v2/_components/form-signup';
import { LoginForm } from 'features/sign-in/v2/page-sign-in';
import { APIAuth } from 'lib/api/firebase/apis/api-auth';
import useSearchParams from 'lib/hooks/use-searchParams';
import { IntegrationImpactLayout } from './_components/layout';

type ResolveUseMutationOptions<FN extends (args: any) => any> =
  UseMutationOptions<Awaited<ReturnType<FN>>, Error, Parameters<FN>[0]>;

function useRegisterAsImPACTClinicianMutation(
  args?: Omit<
    ResolveUseMutationOptions<typeof APIAuth.registerNewUser>,
    'mutationFn'
  >
) {
  return useMutation({
    ...args,
    mutationFn: APIAuth.registerNewUser
  });
}

export function PageIntegrationIMPACTRegisterAsClinician() {
  const [params] = useSearchParams();
  function onSuccess() {
    // history.push({
    //   pathname: paths.integration.impact.join(),
    //   search: location.search
    // });
  }

  return (
    <IntegrationImpactLayout>
      <PrintableRootPage title="">
        <div>
          <QAPanelTemplate>
            <Text>Token from ImPACT: {params.get('token')}</Text>
          </QAPanelTemplate>
          <Card style={{ paddingBottom: '1rem' }}>
            <Flex vertical align="center" gap={16}>
              <Text style={{ fontWeight: 'bold' }}>
                To continue to ConcussionRx you must be signed in.
              </Text>

              <Tabs
                centered
                items={[
                  {
                    key: 'register',
                    label: 'Create an account',
                    children: <Register onSuccess={onSuccess} />
                  },
                  {
                    key: 'signin',
                    label: 'Sign in',
                    children: <LoginForm onSuccess={onSuccess} />
                  }
                ]}
              />
            </Flex>
          </Card>
        </div>
      </PrintableRootPage>
    </IntegrationImpactLayout>
  );
}

interface RegisterProps {
  onSuccess?: () => void;
  onError?: () => void;
}

function Register(props: RegisterProps) {
  const registerMutation = useRegisterAsImPACTClinicianMutation();
  const auth = useAuth(() => !!0);

  async function onFinish(values: SignupFormProps) {
    try {
      registerMutation.reset();
      auth.signInWithCustomToken.reset();

      const token = await registerMutation.mutateAsync({
        displayName: values.name,
        email: values.email,
        password: values.password
      });

      // WARNING: This might fail or succeed
      // The user should only be able to progress if this succeeds
      await auth.signInWithCustomToken.mutateAsync(token.token);

      props.onSuccess?.();
    } catch (_error) {
      props.onError?.();
    }
  }
  return (
    <div>
      {(registerMutation.isLoading || auth.signInWithCustomToken.isLoading) && (
        <Spin />
      )}

      <CoreSignupForm
        onFinish={onFinish}
        initialValues={{
          clinicName: 'ImPACT 2026'
        }}
        actionButtonText="Get Started"
      />

      {auth.signInWithCustomToken.error && (
        <ErrorDisplay
          title="Sign in with token error"
          error={auth.signInWithCustomToken.error as any}
        />
      )}
      {registerMutation.error && (
        <ErrorDisplay
          title="Registration error"
          error={registerMutation.error}
        />
      )}
    </div>
  );
}
